import { format } from 'date-fns'

/**
 * Payment terms, shared by every form that writes them.
 *
 * Mirrors PAYMENT_TERMS_CHOICES / PAYMENT_TERMS_DAYS in
 * backend/apexos/apps/billing/models.py and utils.py. Recurring templates write
 * their terms straight onto the invoices they generate, so a second private
 * list is how "Net 15" typed into one form became 30 days in the other.
 */
export const PAYMENT_TERMS: Array<{ value: string; label: string }> = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_45', label: 'Net 45' },
  { value: 'net_60', label: 'Net 60' },
  { value: 'net_90', label: 'Net 90' },
  { value: 'custom', label: 'Custom' },
]

const PAYMENT_TERMS_DAYS: Record<string, number> = {
  due_on_receipt: 0,
  net_15: 15,
  net_30: 30,
  net_45: 45,
  net_60: 60,
  net_90: 90,
}

/** Due date the terms imply, or '' when they imply none (custom terms). */
export const dueDateFromTerms = (documentDate: string, paymentTerms: string): string => {
  const days = PAYMENT_TERMS_DAYS[paymentTerms]
  if (days === undefined || !documentDate) return ''
  // Parse as local midnight: `new Date('2026-01-31')` is UTC and can format
  // back as the previous day.
  const due = new Date(`${documentDate}T00:00:00`)
  if (Number.isNaN(due.getTime())) return ''
  due.setDate(due.getDate() + days)
  return format(due, 'yyyy-MM-dd')
}

/** Fold a stored value onto a known term, so a picker never shows blank. */
export const normalizePaymentTerms = (paymentTerms: string | null | undefined): string => {
  const value = (paymentTerms || '').trim().toLowerCase().replace(/\s+/g, '_')
  const aliases: Record<string, string> = { immediate: 'due_on_receipt' }
  const resolved = aliases[value] || value
  return PAYMENT_TERMS.some(term => term.value === resolved) ? resolved : 'net_30'
}
